import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserTypeSelectionProps {
  onSelect: (userType: 'job_seeker' | 'employer') => void;
  onBack?: () => void;
}

export const UserTypeSelection = ({ onSelect, onBack }: UserTypeSelectionProps) => {
  const [selectedType, setSelectedType] = useState<'job_seeker' | 'employer' | null>(null);

  const userTypes = [
    {
      type: 'job_seeker' as const,
      title: 'Job Seeker',
      description: 'I\'m looking for career opportunities',
      icon: Users,
      benefits: [
        'AI-powered job matching',
        'Direct applications to employers',
        'Career growth insights',
        'Skill-based recommendations'
      ]
    },
    {
      type: 'employer' as const,
      title: 'Employer',
      description: 'I\'m hiring talented professionals',
      icon: Building2,
      benefits: [
        'Access to qualified candidates',
        'AI-powered candidate matching',
        'Applicant tracking system',
        'Advanced recruitment tools'
      ]
    }
  ];

  const handleContinue = () => {
    if (selectedType) {
      onSelect(selectedType);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2 text-white">I am a...</h2>
        <p className="text-white">Choose your account type to get started</p>
      </div>

      <div className="grid gap-4">
        {userTypes.map((userType) => (
          <Card
            key={userType.type}
            className={cn(
              "cursor-pointer transition-all duration-200 hover:shadow-lg",
              selectedType === userType.type
                ? "ring-2 ring-primary border-primary bg-primary/5"
                : "hover:border-primary/50"
            )}
            onClick={() => setSelectedType(userType.type)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-3">
                <div className={cn(
                  "w-12 h-12 rounded-lg flex items-center justify-center",
                  selectedType === userType.type
                    ? "bg-primary text-white"
                    : "bg-muted text-muted-foreground"
                )}>
                  <userType.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{userType.title}</CardTitle>
                  <CardDescription>{userType.description}</CardDescription>
                </div>
                <div className={cn(
                  "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                  selectedType === userType.type
                    ? "border-primary bg-primary"
                    : "border-muted-foreground"
                )}>
                  {selectedType === userType.type && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-1 text-sm text-muted-foreground">
                {userType.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-primary rounded-full" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => {
            // Update URL and navigate back to sign in
            window.history.pushState({}, '', '/auth');
            window.location.reload();
          }}
          className="flex-1"
        >
          Back to Sign In
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedType}
          className="flex-1"
        >
          Continue
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};